/**
 * MedicationsConversation - Voice-led scripted conversation to review medications.
 * Continuous listening, pause-based turn-taking, TTS with echo filter. Same UX as Symptom Checker.
 * Prompts: intro, per-med (keep/remove/edit), then "any to add?". Parses reply and calls onDelete/onUpdate/onAdd.
 *
 * Add/Edit: user can return to any field (name, dosage, frequency, optional); changes are applied to a draft
 * and the list is only modified after an explicit confirmation step (yes to save, no to change something).
 *
 * Buttons/UI during voice: draft state is local to this component. Options for syncing with form buttons:
 * - Sync: lift draft to parent so form and voice share one source of truth; form edits appear when user says "done".
 * - Form authoritative: when user clicks Save in add/edit form, persist and close; optionally notify conversation to advance.
 * - Pause voice: when user focuses form, show "Editing in form; say continue when ready" and ignore voice until they continue.
 */

import React, { useState, useRef, useEffect, useCallback, useId } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Chip,
  LinearProgress,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import PlayArrowRounded from '@mui/icons-material/PlayArrowRounded';
import StopRounded from '@mui/icons-material/StopRounded';
import AddRounded from '@mui/icons-material/AddRounded';
import CameraAltRounded from '@mui/icons-material/CameraAltRounded';
import MicIcon from '@mui/icons-material/Mic';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import PersonIcon from '@mui/icons-material/Person';
import { speak, unlockAudioContext, stopSpeaking } from 'services/ttsService';
import useEchoFilter, { stripSystemEcho } from '../components/symptomCheck/voice-conversation/useEchoFilter';
import useSymptomLanguage from '../components/symptomCheck/voice-conversation/useSymptomLanguage';
import { DEFAULT_PAUSE_DURATION_MS, ECHO_COOLDOWN_MS, SUPPORTED_LANGUAGES } from '../components/symptomCheck/voice-conversation/constants';
import {
  INTRO_HAVE_MEDS,
  INTRO_NO_MEDS,
  MEDICATION_PROMPT,
  EDIT_WHICH_SECTION,
  EDIT_WHAT_SECTION_VALUE,
  EDIT_WHICH_SECTION_NEXT,
  EDIT_READY_TO_MOVE_ON,
  EDIT_SECTION_SET,
  EDIT_CONFIRM_PROMPT,
  EDIT_AGAIN_PROMPT,
  ADD_PROMPT,
  ALL_VERIFIED_PROMPT,
  ADD_ASK_NAME,
  ADD_ASK_DOSAGE,
  ADD_ASK_FREQUENCY,
  ADD_OPTIONAL_PROMPT,
  ADD_CONFIRM_PROMPT,
  ADD_SAVED,
  DONE_PROMPT,
  DONE_PROMPT_FINAL,
  OFFER_SECTION_PROMPT,
  SECTION_OFFER_NAMES,
  ALLERGIES_INTRO,
  ALLERGY_ITEM_PROMPT,
  PMH_INTRO,
  PMH_ITEM_PROMPT,
  PSH_INTRO,
  PSH_ITEM_PROMPT,
  OTHER_INTRO,
  ALLERGY_SECTION_LABELS,
  ADD_ALLERGY_PROMPT,
  ADD_ALLERGY_ASK_ALLERGEN,
  ADD_ALLERGY_ASK_TYPE,
  ADD_ALLERGY_ASK_REACTION,
  ADD_ALLERGY_OPTIONAL,
  ADD_ALLERGY_CONFIRM,
  ADD_ALLERGY_SAVED,
  EDIT_ALLERGY_CONFIRM_PROMPT,
  PMH_SECTION_LABELS,
  ADD_PMH_PROMPT,
  ADD_PMH_ASK_CONDITION,
  ADD_PMH_ASK_STATUS,
  ADD_PMH_OPTIONAL as ADD_PMH_OPTIONAL_PROMPT,
  ADD_PMH_CONFIRM,
  ADD_PMH_SAVED,
  EDIT_PMH_CONFIRM_PROMPT,
  parsePmhSectionName,
  PSH_SECTION_LABELS,
  ADD_PSH_PROMPT,
  ADD_PSH_ASK_PROCEDURE,
  ADD_PSH_ASK_YEAR,
  ADD_PSH_OPTIONAL as ADD_PSH_OPTIONAL_PROMPT,
  ADD_PSH_CONFIRM,
  ADD_PSH_SAVED,
  EDIT_PSH_CONFIRM_PROMPT,
  parsePshSectionName,
  OTHER_FIELD_TO_KEY,
  OTHER_FIELD_OPTIONS,
  OTHER_WHICH_FIELD,
  OTHER_SUGGEST_OPTIONS,
  OTHER_ASK_CUSTOM,
  OTHER_CONFIRM_FIELD,
  OTHER_FIELD_SAVED,
  parseOtherFieldName,
  parseOtherFieldOption,
  SECTION_LABELS,
  parseMedicationReply,
  parseYesNo,
  parseSectionName,
  parseAllergySectionName,
  parseReadyToMoveOn,
  isLikelyEchoOfAddPrompt,
} from './medicationsConversationPrompts';
import {
  applyMedicationPronunciation,
  correctTranscriptForMedicationName,
} from './medicationVoiceUtils';
import { TOP_FDA_MEDICATION_NAMES } from './topFdaMedicationNames';
import { getConstrainedChoices } from './medicationsConversationChoiceSets';

/** Cooldown after TTS before accepting input. Use same as Symptom Checker for consistent behavior. */
const MEDICATIONS_ECHO_COOLDOWN_MS = ECHO_COOLDOWN_MS;
const DONE_AUTO_END_DELAY_MS = 7_000;

const speechSupported = typeof window !== 'undefined' && ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

/** Strip trailing period that speech recognition often adds to medication field values. */
function trimMedicationFieldValue(value) {
  if (value == null || typeof value !== 'string') return value;
  return value.replace(/\.\s*$/, '').trim();
}

const PHASE_MEDICATIONS = 'medications';
const PHASE_ALLERGIES = 'allergies';
const PHASE_PMH = 'pmh';
const PHASE_PSH = 'psh';
const PHASE_OTHER = 'other';

export default function MedicationsConversation({
  medications = [],
  currentMedIndex = 0,
  onCurrentIndexChange,
  onDelete,
  onUpdate,
  onAdd,
  onRefreshMedications,
  onRequestAddOpen,
  onRequestScanOpen,
  onMedicationsPhaseChange,
  onSectionChange,
  onRequestEditOpen,
  onEditDoneFromVoice,
  onAddDoneFromVoice,
  onEditingDraftChange,
  onAddDraftChange,
  onAddFromVoice,
  onHighlightAddField,
  onHighlightEditField,
  allergies = [],
  pmh = [],
  psh = [],
  other = {},
  onAllergiesUpdate,
  onPmhUpdate,
  onPshUpdate,
  onOtherUpdate,
  medicationsOnly = false,
  formSavedSignal = 0,
  formCancelledSignal = 0,
  onDone,
  onEndConversation,
  initialLanguage,
  autoStart = false,
}) {
  const [step, setStep] = useState('idle');
  const [phase, setPhase] = useState(PHASE_MEDICATIONS);
  const [offerSectionIndex, setOfferSectionIndex] = useState(0);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [waitingForAllergyEditConfirm, setWaitingForAllergyEditConfirm] = useState(false);
  const [waitingForAllergyAddConfirm, setWaitingForAllergyAddConfirm] = useState(false);
  const [addingAllergySubstep, setAddingAllergySubstep] = useState('allergen');
  const [addingAllergyOptionalSection, setAddingAllergyOptionalSection] = useState(null);
  const [waitingForPmhEditConfirm, setWaitingForPmhEditConfirm] = useState(false);
  const [waitingForPmhAddConfirm, setWaitingForPmhAddConfirm] = useState(false);
  const [addingPmhSubstep, setAddingPmhSubstep] = useState('condition');
  const [addingPmhOptionalSection, setAddingPmhOptionalSection] = useState(null);
  const [waitingForPshEditConfirm, setWaitingForPshEditConfirm] = useState(false);
  const [waitingForPshAddConfirm, setWaitingForPshAddConfirm] = useState(false);
  const [addingPshSubstep, setAddingPshSubstep] = useState('procedure');
  const [addingPshOptionalSection, setAddingPshOptionalSection] = useState(null);
  const [otherFieldKey, setOtherFieldKey] = useState(null);
  const [otherFieldValue, setOtherFieldValue] = useState(null);
  const [waitingForOtherConfirm, setWaitingForOtherConfirm] = useState(false);
  const [conversationLog, setConversationLog] = useState([]);
  const cmrVoiceLabelId = useId();
  const [textInput, setTextInput] = useState('');
  const [showTypeInput, setShowTypeInput] = useState(false);
  const [waitingForEditConfirm, setWaitingForEditConfirm] = useState(false);
  const [waitingForAddConfirm, setWaitingForAddConfirm] = useState(false);
  // Alias for any cached/bundle reference to old state name
  const waitingForNext = waitingForEditConfirm;
  const [editingDraft, setEditingDraft] = useState(null);
  const [editingSubstep, setEditingSubstep] = useState('which_section');
  const [editingSection, setEditingSection] = useState(null);
  const [addingDraft, setAddingDraft] = useState(null);
  const [addingSubstep, setAddingSubstep] = useState('name');
  const [addingSection, setAddingSection] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isAcceptingInput, setIsAcceptingInput] = useState(true); // false during echo cooldown after TTS
  const [isActive, setIsActive] = useState(false);
  const [language, setLanguage] = useState(initialLanguage ?? 'en-US');
  const symptomLanguage = useSymptomLanguage({ defaultLanguage: language });
  const symptomLanguageRef = useRef(symptomLanguage);
  symptomLanguageRef.current = symptomLanguage;
  const logEndRef = useRef(null);
  const transcriptAccumulatorRef = useRef('');
  const pauseTimerRef = useRef(null);
  const acceptingInputRef = useRef(true);
  const isSpeakingRef = useRef(false);
  const isActiveRef = useRef(false);
  const recognitionRef = useRef(null);
  const processReplyRef = useRef(null);
  const medicationNameContextRef = useRef({ expectingName: false, candidateNames: [] });
  const modalOpenNotifiedRef = useRef(false);
  const transcriptScrollRef = useRef(null);
  const [speechRecognitionUserHint, setSpeechRecognitionUserHint] = useState(null);

  const echoFilter = useEchoFilter();
  const { addSystemUtterance, startEchoCooldown, endEchoCooldown, isInCooldown, filterEcho, getLastSpokenQuestion } = echoFilter;

  const scrollLog = useCallback(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const addLog = useCallback((role, text) => {
    setConversationLog((prev) => [...prev, { role, text }]);
    setTimeout(scrollLog, 50);
  }, [scrollLog]);

  const speakAndLog = useCallback(
    async (text, medicationNames, options) => {
      // Re-enable input when starting next phrase so we don't stay stuck in "Initializing" during generateSpeech() (onStart runs only when playback starts).
      if (isInCooldown()) {
        acceptingInputRef.current = true;
        setIsAcceptingInput(true);
      }
      const highlightAdd = options?.highlightAddField ?? null;
      const highlightEdit = options?.highlightEditField ?? null;
      onHighlightAddField?.(highlightAdd);
      onHighlightEditField?.(highlightEdit);
      if (highlightAdd != null) onHighlightEditField?.(null);
      if (highlightEdit != null) onHighlightAddField?.(null);
      addLog('system', text);
      let textToSpeak =
        Array.isArray(medicationNames) && medicationNames.length > 0
          ? applyMedicationPronunciation(text, medicationNames)
          : text;
      if (symptomLanguage.needsTranslation) {
        try {
          textToSpeak = await symptomLanguage.translateQuestion(textToSpeak);
        } catch (e) {
          console.warn('[MedicationsConversation] TTS translation failed, using original:', e);
        }
      }
      addSystemUtterance(textToSpeak);
      const langCode = symptomLanguage.language || 'en-US';
      try {
        await speak(textToSpeak, langCode, {
          onStart: () => {
            // Gate input only when playback actually starts so we don't show "Initializing" during slow TTS fetch (e.g. 7s Firebase)
            acceptingInputRef.current = false;
            setIsAcceptingInput(false);
            isSpeakingRef.current = true;
            startEchoCooldown();
            setIsSpeaking(true);
            setIsListening(false);
            if (!modalOpenNotifiedRef.current) {
              modalOpenNotifiedRef.current = true;
              onMedicationsPhaseChange?.(true);
            }
          },
          onEnd: () => {
            isSpeakingRef.current = false;
            setIsSpeaking(false);
            onHighlightAddField?.(null);
            onHighlightEditField?.(null);
            // Restore isListening immediately (recognition is still running in continuous mode; onStart set it false for the "Speaking" chip)
            setIsListening(true);
            endEchoCooldown(MEDICATIONS_ECHO_COOLDOWN_MS, () => {
              acceptingInputRef.current = true;
              setIsAcceptingInput(true);
            });
          },
        });
      } catch (e) {
        console.warn('[MedicationsConversation] TTS error:', e);
        isSpeakingRef.current = false;
        setIsSpeaking(false);
        onHighlightAddField?.(null);
        onHighlightEditField?.(null);
        setIsListening(true);
        endEchoCooldown(MEDICATIONS_ECHO_COOLDOWN_MS, () => {
          acceptingInputRef.current = true;
          setIsAcceptingInput(true);
        });
      }
    },
    [addLog, addSystemUtterance, startEchoCooldown, endEchoCooldown, isInCooldown, onMedicationsPhaseChange, onHighlightAddField, onHighlightEditField, symptomLanguage]
  );

  /** @param reply - English text used for parsing (keep/remove/edit/section names). @param rawForFields - optional; when set (e.g. non-English), use for storing name/dosage/frequency/notes as spoken. */
  const processReply = useCallback(
    (reply, rawForFields) => {
      if (!reply || !reply.trim()) return;
      const forDisplay = rawForFields != null ? rawForFields : reply;
      const forFields = rawForFields != null ? rawForFields : reply;
      addLog('user', forDisplay);
      const parsed = parseMedicationReply(reply);

      if (step === 'review' && phase === PHASE_ALLERGIES) {
        const list = allergies;
        const idx = currentSectionIndex;
        const item = list[idx];
        if (waitingForAllergyEditConfirm) {
          if (parsed === 'keep') {
            const draftToSave = editingDraft;
            setWaitingForAllergyEditConfirm(false);
            setEditingDraft(null);
            setEditingSubstep('which_section');
            setEditingSection(null);
            if (draftToSave && typeof onAllergiesUpdate === 'function') {
              const updated = list.map((a, i) => (i === idx ? draftToSave : a));
              onAllergiesUpdate(updated);
            }
            setCurrentSectionIndex(idx + 1);
            if (idx + 1 >= list.length) {
              setStep('add_prompt');
              speakAndLog(ADD_ALLERGY_PROMPT());
            } else {
              speakAndLog(ALLERGY_ITEM_PROMPT(list[idx + 1], idx + 1, list.length));
            }
          } else if (parsed === 'no') {
            setWaitingForAllergyEditConfirm(false);
            setStep('editing_allergy');
            speakAndLog(EDIT_AGAIN_PROMPT());
          }
          return;
        }
        if (parsed === 'keep') {
          setCurrentSectionIndex(idx + 1);
          if (idx + 1 >= list.length) {
            setStep('add_prompt');
            speakAndLog(ADD_ALLERGY_PROMPT());
          } else {
            speakAndLog(ALLERGY_ITEM_PROMPT(list[idx + 1], idx + 1, list.length));
          }
          return;
        }
        if (parsed === 'remove' && item) {
          const updated = list.filter((_, i) => i !== idx);
          onAllergiesUpdate?.(updated);
          if (updated.length === 0) {
            setStep('add_prompt');
            setCurrentSectionIndex(0);
            speakAndLog(ADD_ALLERGY_PROMPT());
          } else {
            const nextIdx = idx >= updated.length ? updated.length - 1 : idx;
            setCurrentSectionIndex(nextIdx);
            speakAndLog(ALLERGY_ITEM_PROMPT(updated[nextIdx], nextIdx, updated.length));
          }
          return;
        }
        if (parsed === 'edit' && item) {
          setEditingDraft({ ...item });
          setEditingSubstep('which_section');
          setEditingSection(null);
          setStep('editing_allergy');
          speakAndLog(EDIT_WHICH_SECTION());
          return;
        }
        return;
      }

      if (step === 'review' && phase === PHASE_PMH) {
        const list = pmh;
        const idx = currentSectionIndex;
        const item = list[idx];
        if (waitingForPmhEditConfirm) {
          if (parsed === 'keep') {
            const draftToSave = editingDraft;
            setWaitingForPmhEditConfirm(false);
            setEditingDraft(null);
            setEditingSubstep('which_section');
            setEditingSection(null);
            if (draftToSave && typeof onPmhUpdate === 'function') {
              const updated = list.map((a, i) => (i === idx ? draftToSave : a));
              onPmhUpdate(updated);
            }
            setCurrentSectionIndex(idx + 1);
            if (idx + 1 >= list.length) {
              setStep('add_prompt');
              speakAndLog(ADD_PMH_PROMPT());
            } else {
              speakAndLog(PMH_ITEM_PROMPT(list[idx + 1], idx + 1, list.length));
            }
          } else if (parsed === 'no') {
            setWaitingForPmhEditConfirm(false);
            setStep('editing_pmh');
            speakAndLog(EDIT_AGAIN_PROMPT());
          }
          return;
        }
        if (parsed === 'keep') {
          setCurrentSectionIndex(idx + 1);
          if (idx + 1 >= list.length) {
            setStep('add_prompt');
            speakAndLog(ADD_PMH_PROMPT());
          } else {
            speakAndLog(PMH_ITEM_PROMPT(list[idx + 1], idx + 1, list.length));
          }
          return;
        }
        if (parsed === 'remove' && item) {
          const updated = list.filter((_, i) => i !== idx);
          onPmhUpdate?.(updated);
          if (updated.length === 0) {
            setStep('add_prompt');
            setCurrentSectionIndex(0);
            speakAndLog(ADD_PMH_PROMPT());
          } else {
            const nextIdx = idx >= updated.length ? updated.length - 1 : idx;
            setCurrentSectionIndex(nextIdx);
            speakAndLog(PMH_ITEM_PROMPT(updated[nextIdx], nextIdx, updated.length));
          }
          return;
        }
        if (parsed === 'edit' && item) {
          setEditingDraft({ ...item });
          setEditingSubstep('which_section');
          setEditingSection(null);
          setStep('editing_pmh');
          speakAndLog(EDIT_WHICH_SECTION());
          return;
        }
        return;
      }

      if (step === 'review' && phase === PHASE_PSH) {
        const list = psh;
        const idx = currentSectionIndex;
        const item = list[idx];
        if (waitingForPshEditConfirm) {
          if (parsed === 'keep') {
            const draftToSave = editingDraft;
            setWaitingForPshEditConfirm(false);
            setEditingDraft(null);
            setEditingSubstep('which_section');
            setEditingSection(null);
            if (draftToSave && typeof onPshUpdate === 'function') {
              const updated = list.map((a, i) => (i === idx ? draftToSave : a));
              onPshUpdate(updated);
            }
            setCurrentSectionIndex(idx + 1);
            if (idx + 1 >= list.length) {
              setStep('add_prompt');
              speakAndLog(ADD_PSH_PROMPT());
            } else {
              speakAndLog(PSH_ITEM_PROMPT(list[idx + 1], idx + 1, list.length));
            }
          } else if (parsed === 'no') {
            setWaitingForPshEditConfirm(false);
            setStep('editing_psh');
            speakAndLog(EDIT_AGAIN_PROMPT());
          }
          return;
        }
        if (parsed === 'keep') {
          setCurrentSectionIndex(idx + 1);
          if (idx + 1 >= list.length) {
            setStep('add_prompt');
            speakAndLog(ADD_PSH_PROMPT());
          } else {
            speakAndLog(PSH_ITEM_PROMPT(list[idx + 1], idx + 1, list.length));
          }
          return;
        }
        if (parsed === 'remove' && item) {
          const updated = list.filter((_, i) => i !== idx);
          onPshUpdate?.(updated);
          if (updated.length === 0) {
            setStep('add_prompt');
            setCurrentSectionIndex(0);
            speakAndLog(ADD_PSH_PROMPT());
          } else {
            const nextIdx = idx >= updated.length ? updated.length - 1 : idx;
            setCurrentSectionIndex(nextIdx);
            speakAndLog(PSH_ITEM_PROMPT(updated[nextIdx], nextIdx, updated.length));
          }
          return;
        }
        if (parsed === 'edit' && item) {
          setEditingDraft({ ...item });
          setEditingSubstep('which_section');
          setEditingSection(null);
          setStep('editing_psh');
          speakAndLog(EDIT_WHICH_SECTION());
          return;
        }
        return;
      }

      if (step === 'review') {
        const med = medications[currentMedIndex];
        if (waitingForEditConfirm) {
          if (parsed === 'keep') {
            const draftToSave = editingDraft;
            setWaitingForEditConfirm(false);
            setEditingDraft(null);
            setEditingSubstep('which_section');
            setEditingSection(null);
            if (med?.id && draftToSave) onUpdate?.(med.id, draftToSave);
            onEditDoneFromVoice?.();
            onCurrentIndexChange(currentMedIndex + 1);
            if (currentMedIndex + 1 >= medications.length) {
              setStep('add_prompt');
              speakAndLog(ADD_PROMPT());
            } else {
              speakAndLog(MEDICATION_PROMPT(medications[currentMedIndex + 1]), [medications[currentMedIndex + 1]?.name].filter(Boolean));
            }
          } else if (parsed === 'no') {
            setWaitingForEditConfirm(false);
            setStep('editing_medication');
            onRequestEditOpen?.(currentMedIndex, () => {});
            speakAndLog(EDIT_AGAIN_PROMPT());
            // editingDraft unchanged so user can change any section
          }
          return;
        }
        if (parsed === 'all_keep') {
          setStep('add_prompt');
          onCurrentIndexChange(medications.length);
          speakAndLog(ALL_VERIFIED_PROMPT());
          return;
        }
        if (parsed === 'keep') {
          onCurrentIndexChange(currentMedIndex + 1);
          if (currentMedIndex + 1 >= medications.length) {
            setStep('add_prompt');
            speakAndLog(ADD_PROMPT());
          } else {
            speakAndLog(MEDICATION_PROMPT(medications[currentMedIndex + 1]), [medications[currentMedIndex + 1]?.name].filter(Boolean));
          }
          return;
        }
        if (parsed === 'remove' && med?.id) {
          onDelete(med.id);
          if (medications.length <= 1) {
            setStep('add_prompt');
            onCurrentIndexChange(0);
            speakAndLog(ADD_PROMPT());
          } else {
            const nextIndex = currentMedIndex >= medications.length - 1 ? currentMedIndex - 1 : currentMedIndex;
            onCurrentIndexChange(nextIndex);
            const nextMed = currentMedIndex >= medications.length - 1 ? medications[currentMedIndex - 1] : medications[currentMedIndex + 1];
            if (nextMed) speakAndLog(MEDICATION_PROMPT(nextMed), [nextMed?.name].filter(Boolean));
            else {
              setStep('add_prompt');
              speakAndLog(ADD_PROMPT());
            }
          }
          return;
        }
        if (parsed === 'edit') {
          const med = medications[currentMedIndex];
          if (!med?.id) return;
          setEditingDraft({ ...med });
          setEditingSubstep('which_section');
          setEditingSection(null);
          setStep('editing_medication');
          onRequestEditOpen?.(currentMedIndex, () => {});
          speakAndLog(EDIT_WHICH_SECTION());
          return;
        }
      }

      if (step === 'editing_medication') {
        const med = medications[currentMedIndex];
        const draft = editingDraft || {};
        const hasRequired = (d) =>
          (d?.name ?? '').toString().trim() &&
          (d?.dosage ?? '').toString().trim() &&
          (d?.frequency ?? '').toString().trim();

        if (editingSubstep === 'which_section') {
          const sectionKey = parseSectionName(reply);
          if (sectionKey) {
            setEditingSection(sectionKey);
            setEditingSubstep('awaiting_value');
            speakAndLog(EDIT_WHAT_SECTION_VALUE(SECTION_LABELS[sectionKey]), undefined, {
              highlightEditField: sectionKey,
            });
            return;
          }
          if (parseReadyToMoveOn(reply)) {
            if (hasRequired(draft)) {
              setStep('review');
              speakAndLog(EDIT_CONFIRM_PROMPT(draft), [draft?.name].filter(Boolean));
              setWaitingForEditConfirm(true);
              // Keep editingDraft until user confirms yes or no
            } else {
              speakAndLog(
                "We still need name, dosage, and frequency. Say the name of any section you want to edit, or say done to confirm."
              );
            }
            return;
          }
          speakAndLog(EDIT_WHICH_SECTION());
          return;
        }

        if (editingSubstep === 'awaiting_value' && editingSection) {
          const value = trimMedicationFieldValue(forFields);
          const nextDraft = { ...draft, [editingSection]: value };
          setEditingDraft(nextDraft);
          // Do not call onUpdate here; persist only after user confirms at confirmation step
          const label = SECTION_LABELS[editingSection];
          setEditingSection(null);
          setEditingSubstep('which_section');
          const setMsg = EDIT_SECTION_SET(label, value);
          const nextMsg = hasRequired(nextDraft)
            ? ` ${EDIT_READY_TO_MOVE_ON()}`
            : ` ${EDIT_WHICH_SECTION_NEXT()}`;
          speakAndLog(setMsg + nextMsg, editingSection === 'name' ? [value].filter(Boolean) : undefined);
          return;
        }
      }

      if (step === 'editing_allergy') {
        const draft = editingDraft || {};
        const list = allergies;
        const idx = currentSectionIndex;

        if (editingSubstep === 'which_section') {
          const sectionKey = parseAllergySectionName(reply);
          if (sectionKey) {
            setEditingSection(sectionKey);
            setEditingSubstep('awaiting_value');
            speakAndLog(EDIT_WHAT_SECTION_VALUE(ALLERGY_SECTION_LABELS[sectionKey]));
            return;
          }
          if (parseReadyToMoveOn(reply)) {
            setStep('review');
            speakAndLog(EDIT_ALLERGY_CONFIRM_PROMPT(draft));
            setWaitingForAllergyEditConfirm(true);
            return;
          }
          speakAndLog(EDIT_WHICH_SECTION());
          return;
        }

        if (editingSubstep === 'awaiting_value' && editingSection) {
          const value = trimMedicationFieldValue(forFields);
          const nextDraft = { ...draft, [editingSection]: value };
          setEditingDraft(nextDraft);
          const label = ALLERGY_SECTION_LABELS[editingSection];
          setEditingSection(null);
          setEditingSubstep('which_section');
          speakAndLog(EDIT_SECTION_SET(label, value) + ' ' + EDIT_WHICH_SECTION_NEXT());
          return;
        }
      }

      if (step === 'editing_pmh') {
        const draft = editingDraft || {};
        const list = pmh;
        const idx = currentSectionIndex;
        if (editingSubstep === 'which_section') {
          const sectionKey = parsePmhSectionName(reply);
          if (sectionKey) {
            setEditingSection(sectionKey);
            setEditingSubstep('awaiting_value');
            speakAndLog(EDIT_WHAT_SECTION_VALUE(PMH_SECTION_LABELS[sectionKey]));
            return;
          }
          if (parseReadyToMoveOn(reply)) {
            setStep('review');
            speakAndLog(EDIT_PMH_CONFIRM_PROMPT(draft));
            setWaitingForPmhEditConfirm(true);
            return;
          }
          speakAndLog(EDIT_WHICH_SECTION());
          return;
        }
        if (editingSubstep === 'awaiting_value' && editingSection) {
          const value = trimMedicationFieldValue(forFields);
          const nextDraft = { ...draft, [editingSection]: value };
          setEditingDraft(nextDraft);
          const label = PMH_SECTION_LABELS[editingSection];
          setEditingSection(null);
          setEditingSubstep('which_section');
          speakAndLog(EDIT_SECTION_SET(label, value) + ' ' + EDIT_WHICH_SECTION_NEXT());
          return;
        }
      }

      if (step === 'editing_psh') {
        const draft = editingDraft || {};
        const list = psh;
        const idx = currentSectionIndex;
        if (editingSubstep === 'which_section') {
          const sectionKey = parsePshSectionName(reply);
          if (sectionKey) {
            setEditingSection(sectionKey);
            setEditingSubstep('awaiting_value');
            speakAndLog(EDIT_WHAT_SECTION_VALUE(PSH_SECTION_LABELS[sectionKey]));
            return;
          }
          if (parseReadyToMoveOn(reply)) {
            setStep('review');
            speakAndLog(EDIT_PSH_CONFIRM_PROMPT(draft));
            setWaitingForPshEditConfirm(true);
            return;
          }
          speakAndLog(EDIT_WHICH_SECTION());
          return;
        }
        if (editingSubstep === 'awaiting_value' && editingSection) {
          const value = trimMedicationFieldValue(forFields);
          const nextDraft = { ...draft, [editingSection]: value };
          setEditingDraft(nextDraft);
          const label = PSH_SECTION_LABELS[editingSection];
          setEditingSection(null);
          setEditingSubstep('which_section');
          speakAndLog(EDIT_SECTION_SET(label, value) + ' ' + EDIT_WHICH_SECTION_NEXT());
          return;
        }
      }

      if (step === 'adding_allergy') {
        const draft = addingDraft || {};
        if (waitingForAllergyAddConfirm) {
          if (parsed === 'keep') {
            setWaitingForAllergyAddConfirm(false);
            const item = { allergen: draft.allergen || '', type: draft.type || 'other', reaction: draft.reaction || '', notes: draft.notes || '' };
            const newList = [...allergies, item];
            onAllergiesUpdate?.(newList);
            setAddingDraft(null);
            setAddingAllergySubstep('allergen');
            setStep('add_prompt');
            speakAndLog(ADD_ALLERGY_SAVED(draft.allergen) + ' ' + ADD_ALLERGY_PROMPT());
          } else if (parsed === 'no') {
            setWaitingForAllergyAddConfirm(false);
            setAddingAllergySubstep('optional');
            speakAndLog(ADD_ALLERGY_OPTIONAL());
          }
          return;
        }
        if (addingAllergySubstep === 'allergen') {
          const nextDraft = { ...draft, allergen: trimMedicationFieldValue(forFields) };
          setAddingDraft(nextDraft);
          setAddingAllergySubstep('type');
          speakAndLog(ADD_ALLERGY_ASK_TYPE());
          return;
        }
        if (addingAllergySubstep === 'type') {
          const v = trimMedicationFieldValue(forFields).toLowerCase();
          const type = /drug/.test(v) ? 'drug' : /food/.test(v) ? 'food' : /environment/.test(v) ? 'environmental' : 'other';
          const nextDraft = { ...draft, type };
          setAddingDraft(nextDraft);
          setAddingAllergySubstep('reaction');
          speakAndLog(ADD_ALLERGY_ASK_REACTION());
          return;
        }
        if (addingAllergySubstep === 'reaction') {
          const nextDraft = { ...draft, reaction: trimMedicationFieldValue(forFields) };
          setAddingDraft(nextDraft);
          setAddingAllergySubstep('optional');
          speakAndLog(ADD_ALLERGY_OPTIONAL());
          return;
        }
        if (addingAllergySubstep === 'optional') {
          if (addingAllergyOptionalSection) {
            const value = trimMedicationFieldValue(forFields);
            const nextDraft = { ...draft, [addingAllergyOptionalSection]: value };
            setAddingDraft(nextDraft);
            setAddingAllergyOptionalSection(null);
            speakAndLog(EDIT_SECTION_SET(ALLERGY_SECTION_LABELS[addingAllergyOptionalSection], value) + ' ' + ADD_ALLERGY_OPTIONAL());
            return;
          }
          const sectionKey = parseAllergySectionName(reply);
          if (sectionKey) {
            setAddingAllergyOptionalSection(sectionKey);
            speakAndLog(EDIT_WHAT_SECTION_VALUE(ALLERGY_SECTION_LABELS[sectionKey]));
            return;
          }
          if (parseReadyToMoveOn(reply)) {
            setWaitingForAllergyAddConfirm(true);
            speakAndLog(ADD_ALLERGY_CONFIRM(draft));
            return;
          }
          speakAndLog(ADD_ALLERGY_OPTIONAL());
          return;
        }
      }

      if (step === 'adding_pmh') {
        const draft = addingDraft || {};
        if (waitingForPmhAddConfirm) {
          if (parsed === 'keep') {
            setWaitingForPmhAddConfirm(false);
            const item = { condition: draft.condition || '', status: draft.status || 'active', notes: draft.notes || '' };
            onPmhUpdate?.([...pmh, item]);
            setAddingDraft(null);
            setAddingPmhSubstep('condition');
            setStep('add_prompt');
            speakAndLog(ADD_PMH_SAVED(draft.condition) + ' ' + ADD_PMH_PROMPT());
          } else if (parsed === 'no') {
            setWaitingForPmhAddConfirm(false);
            setAddingPmhSubstep('optional');
            speakAndLog(ADD_PMH_OPTIONAL_PROMPT());
          }
          return;
        }
        if (addingPmhSubstep === 'condition') {
          setAddingDraft({ ...draft, condition: trimMedicationFieldValue(forFields) });
          setAddingPmhSubstep('status');
          speakAndLog(ADD_PMH_ASK_STATUS());
          return;
        }
        if (addingPmhSubstep === 'status') {
          const v = trimMedicationFieldValue(forFields).toLowerCase();
          const status = /controlled/.test(v) ? 'controlled' : /resolved/.test(v) ? 'resolved' : 'active';
          setAddingDraft({ ...draft, status });
          setAddingPmhSubstep('optional');
          speakAndLog(ADD_PMH_OPTIONAL_PROMPT());
          return;
        }
        if (addingPmhSubstep === 'optional') {
          if (addingPmhOptionalSection) {
            const value = trimMedicationFieldValue(forFields);
            setAddingDraft({ ...draft, [addingPmhOptionalSection]: value });
            setAddingPmhOptionalSection(null);
            speakAndLog(EDIT_SECTION_SET(PMH_SECTION_LABELS[addingPmhOptionalSection], value) + ' ' + ADD_PMH_OPTIONAL_PROMPT());
            return;
          }
          const sectionKey = parsePmhSectionName(reply);
          if (sectionKey) {
            setAddingPmhOptionalSection(sectionKey);
            speakAndLog(EDIT_WHAT_SECTION_VALUE(PMH_SECTION_LABELS[sectionKey]));
            return;
          }
          if (parseReadyToMoveOn(reply)) {
            setWaitingForPmhAddConfirm(true);
            speakAndLog(ADD_PMH_CONFIRM(draft));
            return;
          }
          speakAndLog(ADD_PMH_OPTIONAL_PROMPT());
          return;
        }
      }

      if (step === 'adding_psh') {
        const draft = addingDraft || {};
        if (waitingForPshAddConfirm) {
          if (parsed === 'keep') {
            setWaitingForPshAddConfirm(false);
            const item = {
              procedure: draft.procedure || '',
              year: draft.year || '',
              hospital: draft.hospital || '',
              surgeon: draft.surgeon || '',
              notes: draft.notes || '',
            };
            onPshUpdate?.([...psh, item]);
            setAddingDraft(null);
            setAddingPshSubstep('procedure');
            setStep('add_prompt');
            speakAndLog(ADD_PSH_SAVED(draft.procedure) + ' ' + ADD_PSH_PROMPT());
          } else if (parsed === 'no') {
            setWaitingForPshAddConfirm(false);
            setAddingPshSubstep('optional');
            speakAndLog(ADD_PSH_OPTIONAL_PROMPT());
          }
          return;
        }
        if (addingPshSubstep === 'procedure') {
          setAddingDraft({ ...draft, procedure: trimMedicationFieldValue(forFields) });
          setAddingPshSubstep('year');
          speakAndLog(ADD_PSH_ASK_YEAR());
          return;
        }
        if (addingPshSubstep === 'year') {
          setAddingDraft({ ...draft, year: trimMedicationFieldValue(forFields) });
          setAddingPshSubstep('optional');
          speakAndLog(ADD_PSH_OPTIONAL_PROMPT());
          return;
        }
        if (addingPshSubstep === 'optional') {
          if (addingPshOptionalSection) {
            const value = trimMedicationFieldValue(forFields);
            setAddingDraft({ ...draft, [addingPshOptionalSection]: value });
            setAddingPshOptionalSection(null);
            speakAndLog(EDIT_SECTION_SET(PSH_SECTION_LABELS[addingPshOptionalSection], value) + ' ' + ADD_PSH_OPTIONAL_PROMPT());
            return;
          }
          const sectionKey = parsePshSectionName(reply);
          if (sectionKey) {
            setAddingPshOptionalSection(sectionKey);
            speakAndLog(EDIT_WHAT_SECTION_VALUE(PSH_SECTION_LABELS[sectionKey]));
            return;
          }
          if (parseReadyToMoveOn(reply)) {
            setWaitingForPshAddConfirm(true);
            speakAndLog(ADD_PSH_CONFIRM(draft));
            return;
          }
          speakAndLog(ADD_PSH_OPTIONAL_PROMPT());
          return;
        }
      }

      if (step === 'add_prompt') {
        if (phase === PHASE_ALLERGIES) {
          if (parsed === 'add') {
            setAddingDraft({});
            setAddingAllergySubstep('allergen');
            setAddingAllergyOptionalSection(null);
            setStep('adding_allergy');
            speakAndLog(ADD_ALLERGY_ASK_ALLERGEN());
            return;
          }
          if (parsed === 'no') {
            setStep('offer_next_section');
            setOfferSectionIndex(1);
            speakAndLog(OFFER_SECTION_PROMPT(SECTION_OFFER_NAMES[1]));
            return;
          }
          return;
        }
        if (phase === PHASE_PMH) {
          if (parsed === 'add') {
            setAddingDraft({});
            setAddingPmhSubstep('condition');
            setAddingPmhOptionalSection(null);
            setStep('adding_pmh');
            speakAndLog(ADD_PMH_ASK_CONDITION());
            return;
          }
          if (parsed === 'no') {
            setStep('offer_next_section');
            setOfferSectionIndex(2);
            speakAndLog(OFFER_SECTION_PROMPT(SECTION_OFFER_NAMES[2]));
            return;
          }
          return;
        }
        if (phase === PHASE_PSH) {
          if (parsed === 'add') {
            setAddingDraft({});
            setAddingPshSubstep('procedure');
            setAddingPshOptionalSection(null);
            setStep('adding_psh');
            speakAndLog(ADD_PSH_ASK_PROCEDURE());
            return;
          }
          if (parsed === 'no') {
            setStep('offer_next_section');
            setOfferSectionIndex(3);
            speakAndLog(OFFER_SECTION_PROMPT(SECTION_OFFER_NAMES[3]));
            return;
          }
          return;
        }
        if (parsed === 'add') {
          setAddingDraft({});
          setAddingSubstep('name');
          setAddingSection(null);
          setStep('adding_medication');
          onRequestAddOpen?.({ voiceMode: true });
          onAddDraftChange?.({});
          speakAndLog(ADD_ASK_NAME(), [], { highlightAddField: 'name' });
          return;
        }
        if (parsed === 'no') {
          if (isLikelyEchoOfAddPrompt(reply)) {
            return;
          }
          if (medicationsOnly) {
            setStep('done');
            setIsActive(false);
            isActiveRef.current = false;
            onMedicationsPhaseChange?.(false);
            onDone?.();
            speakAndLog(DONE_PROMPT());
            return;
          }
          if (phase === PHASE_MEDICATIONS) {
            setStep('offer_next_section');
            setOfferSectionIndex(0);
            speakAndLog(OFFER_SECTION_PROMPT(SECTION_OFFER_NAMES[0]));
          } else {
            const nextIndex =
              phase === PHASE_ALLERGIES ? 1 : phase === PHASE_PMH ? 2 : phase === PHASE_PSH ? 3 : 4;
            setStep('offer_next_section');
            setOfferSectionIndex(nextIndex);
            if (nextIndex > 3) {
              setStep('done');
              setIsActive(false);
              isActiveRef.current = false;
              onMedicationsPhaseChange?.(false);
              onDone?.();
              speakAndLog(DONE_PROMPT_FINAL());
            } else {
              speakAndLog(OFFER_SECTION_PROMPT(SECTION_OFFER_NAMES[nextIndex]));
            }
          }
          return;
        }
      }

      if (step === 'offer_next_section') {
        const yn = parseYesNo(reply);
        if (yn === 'yes') {
          if (offerSectionIndex === 0) {
            setPhase(PHASE_ALLERGIES);
            setCurrentSectionIndex(0);
            const list = allergies;
            if (list.length > 0) {
              setStep('review');
              speakAndLog(ALLERGIES_INTRO(list.length));
              speakAndLog(ALLERGY_ITEM_PROMPT(list[0], 0, list.length));
            } else {
              setStep('add_prompt');
              speakAndLog(ALLERGIES_INTRO(0));
            }
          } else if (offerSectionIndex === 1) {
            setPhase(PHASE_PMH);
            setCurrentSectionIndex(0);
            const list = pmh;
            if (list.length > 0) {
              setStep('review');
              speakAndLog(PMH_INTRO(list.length));
              speakAndLog(PMH_ITEM_PROMPT(list[0], 0, list.length));
            } else {
              setStep('add_prompt');
              speakAndLog(PMH_INTRO(0));
            }
          } else if (offerSectionIndex === 2) {
            setPhase(PHASE_PSH);
            setCurrentSectionIndex(0);
            const list = psh;
            if (list.length > 0) {
              setStep('review');
              speakAndLog(PSH_INTRO(list.length));
              speakAndLog(PSH_ITEM_PROMPT(list[0], 0, list.length));
            } else {
              setStep('add_prompt');
              speakAndLog(PSH_INTRO(0));
            }
          } else {
            setPhase(PHASE_OTHER);
            setStep('other_pick_field');
            speakAndLog(OTHER_INTRO());
          }
          return;
        }
        if (yn === 'no') {
          const nextIndex = offerSectionIndex + 1;
          setOfferSectionIndex(nextIndex);
          if (nextIndex > 3) {
            setStep('done');
            setIsActive(false);
            isActiveRef.current = false;
            onMedicationsPhaseChange?.(false);
            onDone?.();
            speakAndLog(DONE_PROMPT_FINAL());
          } else {
            speakAndLog(OFFER_SECTION_PROMPT(SECTION_OFFER_NAMES[nextIndex]));
          }
          return;
        }
      }

      const otherFieldLabel = (key) =>
        key === 'smoking' ? 'smoking' : key === 'alcohol' ? 'alcohol' : key === 'family_history' ? 'family history' : key === 'notes' ? 'notes' : key;

      if (step === 'other_confirm') {
        const yn = parseYesNo(reply);
        if (yn === 'yes') {
          setWaitingForOtherConfirm(false);
          const storageKey = OTHER_FIELD_TO_KEY[otherFieldKey];
          if (storageKey && typeof onOtherUpdate === 'function') {
            const merged = { ...other, [storageKey]: otherFieldValue };
            onOtherUpdate(merged);
          }
          setOtherFieldKey(null);
          setOtherFieldValue(null);
          setStep('other_pick_field');
          speakAndLog(OTHER_FIELD_SAVED(otherFieldLabel(otherFieldKey)) + ' ' + OTHER_WHICH_FIELD());
        } else if (yn === 'no') {
          setWaitingForOtherConfirm(false);
          setOtherFieldKey(null);
          setOtherFieldValue(null);
          setStep('other_pick_field');
          speakAndLog(OTHER_WHICH_FIELD());
        }
        return;
      }

      if (step === 'other_awaiting_custom_value') {
        const value = trimMedicationFieldValue(forFields);
        setOtherFieldValue(value || reply);
        setStep('other_confirm');
        setWaitingForOtherConfirm(true);
        speakAndLog(OTHER_CONFIRM_FIELD(otherFieldLabel(otherFieldKey), value || reply));
        return;
      }

      if (step === 'other_suggest_options') {
        const options = OTHER_FIELD_OPTIONS[otherFieldKey];
        const chosen = parseOtherFieldOption(reply, otherFieldKey, options);
        if (chosen === '__custom__') {
          setStep('other_awaiting_custom_value');
          speakAndLog(OTHER_ASK_CUSTOM(otherFieldLabel(otherFieldKey)));
          return;
        }
        if (chosen) {
          setOtherFieldValue(chosen);
          setStep('other_confirm');
          setWaitingForOtherConfirm(true);
          speakAndLog(OTHER_CONFIRM_FIELD(otherFieldLabel(otherFieldKey), chosen));
          return;
        }
        setOtherFieldValue(trimMedicationFieldValue(forFields) || reply);
        setStep('other_confirm');
        setWaitingForOtherConfirm(true);
        speakAndLog(OTHER_CONFIRM_FIELD(otherFieldLabel(otherFieldKey), trimMedicationFieldValue(forFields) || reply));
        return;
      }

      if (step === 'other_pick_field') {
        const field = parseOtherFieldName(reply);
        if (field === 'done') {
          setStep('done');
          setIsActive(false);
          isActiveRef.current = false;
          onMedicationsPhaseChange?.(false);
          onDone?.();
          speakAndLog(DONE_PROMPT_FINAL());
          return;
        }
        if (field && field !== 'done') {
          setOtherFieldKey(field);
          setOtherFieldValue(null);
          const options = OTHER_FIELD_OPTIONS[field];
          setStep('other_suggest_options');
          speakAndLog(OTHER_SUGGEST_OPTIONS(otherFieldLabel(field), options));
          return;
        }
        speakAndLog(OTHER_WHICH_FIELD());
        return;
      }

      if (step === 'adding_medication') {
        const draft = addingDraft || {};
        const hasRequired = (d) =>
          (d?.name ?? '').toString().trim() &&
          (d?.dosage ?? '').toString().trim() &&
          (d?.frequency ?? '').toString().trim();
        const optionalSectionKeys = ['prescribedBy', 'pharmacy', 'notes'];

        if (waitingForAddConfirm) {
          if (parsed === 'keep') {
            setWaitingForAddConfirm(false);
            onAddFromVoice?.(draft);
            setStep('add_prompt');
            setAddingDraft(null);
            setAddingSubstep('name');
            setAddingSection(null);
            speakAndLog(ADD_SAVED(draft.name) + ' ' + ADD_PROMPT(), [draft.name].filter(Boolean));
          } else if (parsed === 'no') {
            setWaitingForAddConfirm(false);
            setAddingSubstep('optional');
            speakAndLog(
              ADD_OPTIONAL_PROMPT(),
              [],
              { highlightAddField: null }
            );
          }
          return;
        }

        if (addingSubstep === 'name') {
          const nextDraft = { ...draft, name: trimMedicationFieldValue(forFields) };
          setAddingDraft(nextDraft);
          onAddDraftChange?.(nextDraft);
          setAddingSubstep('dosage');
          speakAndLog(ADD_ASK_DOSAGE(), [], { highlightAddField: 'dosage' });
          return;
        }
        if (addingSubstep === 'dosage') {
          const nextDraft = { ...draft, dosage: trimMedicationFieldValue(forFields) };
          setAddingDraft(nextDraft);
          onAddDraftChange?.(nextDraft);
          setAddingSubstep('frequency');
          speakAndLog(ADD_ASK_FREQUENCY(), [], { highlightAddField: 'frequency' });
          return;
        }
        if (addingSubstep === 'frequency') {
          const nextDraft = { ...draft, frequency: trimMedicationFieldValue(forFields) };
          setAddingDraft(nextDraft);
          onAddDraftChange?.(nextDraft);
          setAddingSubstep('optional');
          speakAndLog(ADD_OPTIONAL_PROMPT(), [], { highlightAddField: null });
          return;
        }
        if (addingSubstep === 'optional') {
          if (addingSection) {
            const value = trimMedicationFieldValue(forFields);
            const nextDraft = { ...draft, [addingSection]: value };
            setAddingDraft(nextDraft);
            onAddDraftChange?.(nextDraft);
            setAddingSection(null);
            const label = SECTION_LABELS[addingSection];
            speakAndLog(EDIT_SECTION_SET(label, value) + ' ' + ADD_OPTIONAL_PROMPT());
            return;
          }
          const sectionKey = parseSectionName(reply);
          if (sectionKey) {
            setAddingSection(sectionKey);
            speakAndLog(EDIT_WHAT_SECTION_VALUE(SECTION_LABELS[sectionKey]), undefined, {
              highlightAddField: sectionKey,
            });
            return;
          }
          if (parseReadyToMoveOn(reply)) {
            if (hasRequired(draft)) {
              setWaitingForAddConfirm(true);
              speakAndLog(ADD_CONFIRM_PROMPT(draft), [draft.name].filter(Boolean));
            } else {
              speakAndLog("We still need name, dosage, and frequency. Say the name of any section you want to edit, or say done to save.", [], {
                highlightAddField: 'name',
              });
              setAddingSubstep('name');
            }
            return;
          }
          speakAndLog(ADD_OPTIONAL_PROMPT(), [], { highlightAddField: null });
          return;
        }
      }
    },
    [
      step,
      currentMedIndex,
      medications,
      addLog,
      onCurrentIndexChange,
      onDelete,
      onRequestAddOpen,
      onMedicationsPhaseChange,
      speakAndLog,
      onRequestEditOpen,
      waitingForEditConfirm,
      waitingForAddConfirm,
      editingDraft,
      editingSubstep,
      editingSection,
      onUpdate,
      onEditDoneFromVoice,
      addingDraft,
      addingSubstep,
      addingSection,
      onAddDraftChange,
      onAddFromVoice,
      phase,
      offerSectionIndex,
      allergies,
      pmh,
      psh,
      onAllergiesUpdate,
      waitingForAllergyEditConfirm,
      waitingForAllergyAddConfirm,
      addingAllergySubstep,
      addingAllergyOptionalSection,
      onPmhUpdate,
      onPshUpdate,
      waitingForPmhEditConfirm,
      waitingForPmhAddConfirm,
      addingPmhSubstep,
      addingPmhOptionalSection,
      waitingForPshEditConfirm,
      waitingForPshAddConfirm,
      addingPshSubstep,
      addingPshOptionalSection,
      other,
      onOtherUpdate,
      otherFieldKey,
      otherFieldValue,
      waitingForOtherConfirm,
      medicationsOnly,
    ]
  );

  processReplyRef.current = processReply;

  useEffect(() => {
    const expectingNameForEdit =
      step === 'editing_medication' &&
      editingSubstep === 'awaiting_value' &&
      editingSection === 'name';
    const expectingNameForAdd =
      step === 'adding_medication' && addingSubstep === 'name';
    const expectingName = expectingNameForEdit || expectingNameForAdd;
    const userNames = medications.map((m) => m?.name).filter(Boolean);
    const candidateNames = [...new Set([...userNames, ...TOP_FDA_MEDICATION_NAMES])];
    medicationNameContextRef.current = { expectingName, candidateNames };
  }, [step, editingSubstep, editingSection, addingSubstep, medications]);

  // Sync voice-editing draft to parent so Edit Medication modal can show live changes (e.g. name → Flagyl)
  useEffect(() => {
    onEditingDraftChange?.(editingDraft);
  }, [editingDraft, onEditingDraftChange]);

  // Notify parent of current CMR section and index so the context panel can show the right content.
  useEffect(() => {
    if (step === 'idle' || !isActive) {
      onSectionChange?.(null, 0);
      return;
    }
    if (medicationsOnly) {
      onSectionChange?.('medications', currentMedIndex);
      return;
    }
    if (phase === PHASE_MEDICATIONS) {
      onSectionChange?.('medications', currentMedIndex);
      return;
    }
    if (phase === PHASE_ALLERGIES) {
      onSectionChange?.('allergies', currentSectionIndex);
      return;
    }
    if (phase === PHASE_PMH) {
      onSectionChange?.('pmh', currentSectionIndex);
      return;
    }
    if (phase === PHASE_PSH) {
      onSectionChange?.('psh', currentSectionIndex);
      return;
    }
    if (phase === PHASE_OTHER) {
      onSectionChange?.('other', currentSectionIndex);
      return;
    }
  }, [step, isActive, phase, currentMedIndex, currentSectionIndex, medicationsOnly, onSectionChange]);

  // ---------- Form button → conversation bridge ----------
  // When the user clicks Save/Update on the MedicationForm dialog, the parent
  // increments formSavedSignal. React to it here so the conversation advances
  // (same as if the user said "yes" by voice).
  useEffect(() => {
    if (formSavedSignal === 0) return; // skip initial render
    if (!isActiveRef.current) return;  // voice conversation not running

    if (step === 'editing_medication' || (step === 'review' && waitingForEditConfirm)) {
      // Data already persisted by ReviewHistoryPage.handleEditFormSave.
      // Stop any in-progress TTS so it doesn't talk over the next prompt.
      stopSpeaking();
      isSpeakingRef.current = false;
      setIsSpeaking(false);

      addLog('user', '(Saved via form)');
      setWaitingForEditConfirm(false);
      setEditingDraft(null);
      setEditingSubstep('which_section');
      setEditingSection(null);
      onEditDoneFromVoice?.();
      onHighlightAddField?.(null);
      onHighlightEditField?.(null);

      // Advance to next medication or add_prompt
      const nextIndex = currentMedIndex + 1;
      onCurrentIndexChange(nextIndex);
      if (nextIndex >= medications.length) {
        setStep('add_prompt');
        speakAndLog(ADD_PROMPT());
      } else {
        setStep('review');
        speakAndLog(MEDICATION_PROMPT(medications[nextIndex]), [medications[nextIndex]?.name].filter(Boolean));
      }
      return;
    }

    if (step === 'adding_medication' || (step === 'adding_medication' && waitingForAddConfirm)) {
      // Data already persisted by ReviewHistoryPage.handleAddFormSave.
      stopSpeaking();
      isSpeakingRef.current = false;
      setIsSpeaking(false);

      const savedName = addingDraft?.name || 'medication';
      addLog('user', '(Saved via form)');
      setWaitingForAddConfirm(false);
      setAddingDraft(null);
      setAddingSubstep('name');
      setAddingSection(null);
      onAddDoneFromVoice?.();
      onHighlightAddField?.(null);
      onHighlightEditField?.(null);

      setStep('add_prompt');
      speakAndLog(ADD_SAVED(savedName) + ' ' + ADD_PROMPT());
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formSavedSignal]);

  // When the user clicks Cancel on the MedicationForm dialog, the parent
  // increments formCancelledSignal. React here so the conversation reverts
  // and re-prompts (same as if the user said "no" / cancelled by voice).
  useEffect(() => {
    if (formCancelledSignal === 0) return; // skip initial render
    if (!isActiveRef.current) return;       // voice conversation not running

    if (step === 'editing_medication' || (step === 'review' && waitingForEditConfirm)) {
      stopSpeaking();
      isSpeakingRef.current = false;
      setIsSpeaking(false);

      addLog('user', '(Cancelled via form)');
      setWaitingForEditConfirm(false);
      setEditingDraft(null);
      setEditingSubstep('which_section');
      setEditingSection(null);
      onEditDoneFromVoice?.();
      onHighlightAddField?.(null);
      onHighlightEditField?.(null);

      // Re-prompt the current medication
      const med = medications[currentMedIndex];
      if (med) {
        setStep('review');
        speakAndLog(MEDICATION_PROMPT(med), [med?.name].filter(Boolean));
      } else {
        setStep('add_prompt');
        speakAndLog(ADD_PROMPT());
      }
      return;
    }

    if (step === 'adding_medication') {
      stopSpeaking();
      isSpeakingRef.current = false;
      setIsSpeaking(false);

      addLog('user', '(Cancelled via form)');
      setWaitingForAddConfirm(false);
      setAddingDraft(null);
      setAddingSubstep('name');
      setAddingSection(null);
      onAddDoneFromVoice?.();
      onHighlightAddField?.(null);
      onHighlightEditField?.(null);

      setStep('add_prompt');
      speakAndLog(ADD_PROMPT());
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formCancelledSignal]);

  const handleStart = useCallback(async () => {
    unlockAudioContext();
    const nextStep = medications.length > 0 ? 'review' : 'add_prompt';
    setStep(nextStep);
    setIsActive(true);
    isActiveRef.current = true;
    // Do NOT set isListening here: first phase is TTS. Chip shows "Initializing…" until TTS onStart, then "Speaking…", then "Listening…" after TTS ends.
    if (speechSupported && recognitionRef.current) {
      try {
        recognitionRef.current.start();
      } catch (e) {
        console.warn('[MedicationsConversation] Recognition start error:', e);
      }
    }
    if (medications.length > 0) {
      onCurrentIndexChange(0);
      await speakAndLog(INTRO_HAVE_MEDS(medications.length));
      speakAndLog(MEDICATION_PROMPT(medications[0]), [medications[0]?.name].filter(Boolean));
    } else {
      await speakAndLog(INTRO_NO_MEDS());
      speakAndLog(ADD_PROMPT());
    }
  }, [medications.length, medications, onCurrentIndexChange, speakAndLog]);

  const handleEnd = useCallback(() => {
    isActiveRef.current = false;
    setIsActive(false);
    setIsAcceptingInput(true); // reset so next start is clean
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (_) {}
      setIsListening(false);
    }
    modalOpenNotifiedRef.current = false;
    setSpeechRecognitionUserHint(null);
    onMedicationsPhaseChange?.(false);
    onSectionChange?.(null, 0);
    if (step === 'editing_medication') {
      setEditingDraft(null);
      setEditingSubstep('which_section');
      setEditingSection(null);
      onEditDoneFromVoice?.();
    }
    if (step === 'adding_medication') {
      setAddingDraft(null);
      setAddingSubstep('name');
      setAddingSection(null);
      onAddDoneFromVoice?.();
    }
    setConversationLog([]); // Clear transcript so next conversation starts blank
    transcriptAccumulatorRef.current = '';
    setStep('idle');
    onEndConversation?.();
  }, [step, onMedicationsPhaseChange, onSectionChange, onEditDoneFromVoice, onAddDoneFromVoice, onEndConversation]);

  useEffect(() => {
    if (step !== 'done') return undefined;
    const timeoutId = setTimeout(() => {
      handleEnd();
    }, DONE_AUTO_END_DELAY_MS);
    return () => clearTimeout(timeoutId);
  }, [step, handleEnd]);

  // When coming from My Health Records "Start conversation", jump straight into the conversation
  const autoStartDoneRef = useRef(false);
  useEffect(() => {
    if (autoStart && !autoStartDoneRef.current && step === 'idle') {
      autoStartDoneRef.current = true;
      handleStart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart]);

  const handleSubmitText = useCallback(() => {
    const t = textInput.trim();
    if (!t) return;
    setTextInput('');
    const ctx = medicationNameContextRef.current;
    const toProcess =
      ctx.expectingName && ctx.candidateNames.length > 0
        ? correctTranscriptForMedicationName(t, ctx.candidateNames).corrected
        : t;
    processReply(toProcess);
  }, [textInput, processReply]);

  const handleChoiceClick = useCallback((sendText) => {
    setTextInput('');
    processReply(sendText);
  }, [processReply]);

  const constrainedChoices = getConstrainedChoices({
    step,
    phase,
    medicationsCount: medications.length,
    waitingForEditConfirm,
    waitingForAllergyEditConfirm,
    waitingForPmhEditConfirm,
    waitingForPshEditConfirm,
    waitingForAddConfirm,
    waitingForAllergyAddConfirm,
    waitingForPmhAddConfirm,
    waitingForPshAddConfirm,
  });

  useEffect(() => {
    if (!constrainedChoices) return;
    setShowTypeInput(false);
    setTextInput('');
  }, [constrainedChoices]);

  // Continuous speech recognition with pause detection
  useEffect(() => {
    if (!speechSupported) return;
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    let effectActive = true;

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = symptomLanguage.getWebSpeechLanguage();

    recognition.onresult = (event) => {
      if (!acceptingInputRef.current || isSpeakingRef.current || isInCooldown()) return;
      if (!isActiveRef.current) return;

      let finalTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          finalTranscript += event.results[i][0].transcript;
        }
      }
      if (!finalTranscript.trim()) return;

      if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
      transcriptAccumulatorRef.current = (transcriptAccumulatorRef.current + ' ' + finalTranscript).trim();

      pauseTimerRef.current = setTimeout(async () => {
        pauseTimerRef.current = null;
        const raw = transcriptAccumulatorRef.current;
        transcriptAccumulatorRef.current = '';
        const cleaned = filterEcho(raw);
        if (!cleaned || !cleaned.trim() || !processReplyRef.current) return;
        const ctx = medicationNameContextRef.current;
        const toProcess =
          ctx.expectingName && ctx.candidateNames.length > 0
            ? correctTranscriptForMedicationName(cleaned.trim(), ctx.candidateNames).corrected
            : cleaned.trim();
        const sl = symptomLanguageRef.current;
        // When non-English, detect echo by comparing translated transcript to translated last TTS (STT wording often differs from TTS)
        if (sl?.needsTranslation && toProcess.trim().length > 30) {
          const lastUtterance = getLastSpokenQuestion?.();
          if (lastUtterance) {
            try {
              const engTranscript = await sl.translateToEnglish(toProcess);
              const engUtterance = await sl.translateToEnglish(lastUtterance);
              const echoCleaned = stripSystemEcho(engTranscript, [engUtterance]);
              if (echoCleaned === '' && engTranscript.length > 20) {
                return;
              }
            } catch (e) {
              console.warn('[MedicationsConversation] Echo check translation failed:', e);
            }
          }
        }
        let replyForParsing = toProcess;
        if (sl?.needsTranslation) {
          try {
            replyForParsing = await sl.translateToEnglish(toProcess);
          } catch (e) {
            console.warn('[MedicationsConversation] translateToEnglish failed, using raw:', e);
          }
        }
        processReplyRef.current(replyForParsing, sl?.needsTranslation ? toProcess : undefined);
      }, DEFAULT_PAUSE_DURATION_MS);
    };

    recognition.onstart = () => {
      if (effectActive) setSpeechRecognitionUserHint(null);
    };

    recognition.onend = () => {
      // Only show "not listening" when we're actually stopping; avoid flashing Initializing when auto-restarting
      if (!isActiveRef.current) {
        setIsListening(false);
        return;
      }
      setTimeout(() => {
        if (isActiveRef.current && recognitionRef.current) {
          try {
            recognitionRef.current.start();
            setIsListening(true);
          } catch (_) {
            setIsListening(false);
          }
        }
      }, 100);
    };

    recognition.onerror = (e) => {
      if (e.error === 'no-speech' || e.error === 'aborted') return;

      // Chrome often emits `network` for transient STT service issues; do not log — it alarms users with stable Wi‑Fi.
      if (e.error === 'network') {
        if (effectActive && isActiveRef.current) {
          setSpeechRecognitionUserHint(
            'Voice recognition could not reach the network. Check your internet connection, try again in a moment, or type your reply in the box below.'
          );
        }
        return;
      }

      if (e.error === 'not-allowed' && effectActive && isActiveRef.current) {
        setSpeechRecognitionUserHint(
          'Microphone access was blocked. Allow the microphone for this site, or type your reply in the box below.'
        );
      }

      console.warn('[MedicationsConversation] Recognition error:', e.error);
    };

    recognitionRef.current = recognition;
    return () => {
      effectActive = false;
      if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
      try {
        recognition.stop();
      } catch (_) {}
      recognitionRef.current = null;
    };
  }, [isInCooldown, filterEcho, symptomLanguage.language, getLastSpokenQuestion]);

  const lastSystem = conversationLog.filter((e) => e.role === 'system').slice(-1)[0];
  const currentPrompt = lastSystem?.text;

  useEffect(() => {
    if (!transcriptScrollRef.current) return;
    requestAnimationFrame(() => {
      if (transcriptScrollRef.current) {
        transcriptScrollRef.current.scrollTop = transcriptScrollRef.current.scrollHeight;
      }
    });
  }, [conversationLog]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {step === 'idle' && (
        <Box sx={{ py: 2, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 1 }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id={cmrVoiceLabelId}>Language</InputLabel>
              <Select
                labelId={cmrVoiceLabelId}
                id={cmrVoiceLabelId + '-select'}
                value={language}
                label="Language"
                onChange={(e) => setLanguage(e.target.value)}
              >
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <MenuItem key={lang.code} value={lang.code}>
                    {lang.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              color="primary"
              startIcon={<PlayArrowRounded />}
              onClick={handleStart}
              size="large"
            >
              Start conversation
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            I will review the key elements of your Continuous Medical Record with you. To keep it easy for you, we'll have a conversation - you may not even have to type!
          </Typography>
        </Box>
      )}

      {step !== 'idle' && (
        <>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {isSpeaking ? (
              <Chip
                icon={<VolumeUpIcon />}
                label="Speaking…"
                color="secondary"
                variant="filled"
                sx={{ fontSize: '0.95rem', fontWeight: 600, py: 1.25, px: 1.5, boxShadow: 1 }}
              />
            ) : isListening && !isAcceptingInput ? (
              <Chip
                icon={<MicIcon />}
                label="Initializing…"
                color="warning"
                variant="filled"
                sx={{ fontSize: '0.95rem', fontWeight: 600, py: 1.25, px: 1.5, boxShadow: 1 }}
                title="Waiting before listening"
              />
            ) : isListening ? (
              <Chip
                icon={<MicIcon />}
                label="Listening…"
                color="primary"
                variant="filled"
                sx={{ fontSize: '0.95rem', fontWeight: 600, py: 1.25, px: 1.5, boxShadow: 1 }}
              />
            ) : (
              <Chip
                icon={<MicIcon />}
                label="Initializing…"
                color="warning"
                variant="filled"
                sx={{ fontSize: '0.95rem', fontWeight: 600, py: 1.25, px: 1.5, boxShadow: 1 }}
                title="Preparing voice"
              />
            )}
            {(step === 'review' || step === 'add_prompt' || step === 'editing_medication' || step === 'editing_allergy' || step === 'editing_pmh' || step === 'editing_psh' || step === 'adding_medication' || step === 'adding_allergy' || step === 'adding_pmh' || step === 'adding_psh' || step === 'offer_next_section' || step === 'other_pick_field' || step === 'other_suggest_options' || step === 'other_awaiting_custom_value' || step === 'other_confirm' || step === 'done') && (
              <Button size="small" variant="outlined" color="primary" startIcon={<StopRounded />} onClick={handleEnd}>
                End conversation
              </Button>
            )}
          </Box>

          {speechRecognitionUserHint && (
            <Alert
              severity="warning"
              sx={{ mb: 1.5 }}
              onClose={() => setSpeechRecognitionUserHint(null)}
            >
              {speechRecognitionUserHint}
            </Alert>
          )}

          {currentPrompt && (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: 'action.hover' }}>
              <Typography variant="body2" fontWeight={500}>
                {currentPrompt}
              </Typography>
            </Paper>
          )}

          <Paper
            variant="outlined"
            sx={{
              flex: 1,
              minHeight: 200,
              maxHeight: 360,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'grey.50',
            }}
          >
            <Typography
              variant="overline"
              sx={{ px: 2, pt: 1.5, pb: 0.5, color: 'text.secondary', fontWeight: 600 }}
            >
              Conversation
            </Typography>
            <Box
              ref={transcriptScrollRef}
              sx={{ flex: 1, overflow: 'auto', px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}
            >
              {conversationLog.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 2 }}>
                  Your conversation will appear here.
                </Typography>
              )}
              {conversationLog.map((entry, i) => (
                <Box
                  key={i}
                  sx={{
                    alignSelf: entry.role === 'system' ? 'flex-start' : 'flex-end',
                    maxWidth: '92%',
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mb: 0.25,
                      ...(entry.role === 'user' && { justifyContent: 'flex-end' }),
                    }}
                  >
                    {entry.role === 'system' ? <VolumeUpIcon sx={{ fontSize: 14 }} /> : <PersonIcon sx={{ fontSize: 14 }} />}
                    {entry.role === 'system' ? 'Assistant' : 'You'}
                  </Typography>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 1.5,
                      py: 1,
                      bgcolor: entry.role === 'system' ? 'white' : 'primary.50',
                      border: '1px solid',
                      borderColor: entry.role === 'system' ? 'divider' : 'primary.200',
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {entry.text}
                    </Typography>
                  </Paper>
                </Box>
              ))}
              <div ref={logEndRef} />
            </Box>
          </Paper>

          {step !== 'idle' && step !== 'done' && (
            <Box sx={{ mt: 2 }}>
              {constrainedChoices ? (
                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                  {constrainedChoices.map((choice) => (
                    <Button
                      key={`${step}-${phase}-${choice.sendText}`}
                      variant="outlined"
                      size="small"
                      onClick={() => handleChoiceClick(choice.sendText)}
                      disabled={isSpeaking}
                    >
                      {choice.label}
                    </Button>
                  ))}
                </Box>
              ) : (
                <>
                  <Button size="small" variant="text" onClick={() => setShowTypeInput((v) => !v)}>
                    {showTypeInput ? 'Hide' : 'Type your response'}
                  </Button>
                  <Collapse in={showTypeInput}>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
                      <TextField
                        size="small"
                        placeholder="keep, remove, edit, add, no..."
                        value={textInput}
                        onChange={(e) => setTextInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSubmitText()}
                        sx={{ flex: 1 }}
                        disabled={isSpeaking}
                      />
                      <Button variant="contained" onClick={handleSubmitText} disabled={!textInput.trim() || isSpeaking}>
                        Send
                      </Button>
                    </Box>
                  </Collapse>
                </>
              )}
              {step === 'add_prompt' && (
                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                  <Button variant="outlined" size="small" startIcon={<AddRounded />} onClick={onRequestAddOpen}>
                    Add by typing
                  </Button>
                  {onRequestScanOpen && (
                    <Button variant="outlined" size="small" startIcon={<CameraAltRounded />} onClick={onRequestScanOpen}>
                      Scan pill bottle
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          )}

          {step === 'done' && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Review complete. You can add or edit medications anytime from the panel.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}
